/**
 * Vehicle trade data wizard tech section
 *
 * @author Fabio Dal Santo <fabio@gestionaleauto.com>
 */

/**
 * Main
 */
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Redux
 */
import { useSelector, useDispatch } from 'react-redux';

/**
 * Redux slices
 */
import { lookupsState } from '../../../redux/lookupsSlice';
import {
  leadState, updateQuoteData, updateQuoteStepValidation,
} from '../../../redux/leadSlice';

/**
 * Helpers
 */
import formatters from '../../../helpers/formatters';

/**
 * Icons
 */
import {
  GasStation, BodyType, Gears, Transmission, Doors, Speedometer, Configuration,
} from '../../../components/SVGIcons';

/**
 * Components
 */
import TechLookup from './TechLookup';

/**
 * @returns {JSX.Element}
 */
function Tech() {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { quote } = useSelector(leadState);
  const {
    techFuel, techBody,
    techDoors, techDisplacement, techPower, techGearbox, versionId, versionCustom,
  } = quote;

  const { quote: quoteLookups } = useSelector(lookupsState);
  const { versions: rawVersionsData } = quoteLookups;

  const getQuoteKeys = (lookupKey) => {
    if (lookupKey === 'version') {
      return {
        valueKey: 'versionId',
        textKey: 'versionText',
        customKey: 'versionCustom',
      };
    }

    return {
      valueKey: `tech${lookupKey.charAt(0).toUpperCase() + lookupKey.slice(1)}`,
      textKey: `tech${lookupKey.charAt(0).toUpperCase() + lookupKey.slice(1)}Text`,
      customKey: '',
    };
  };

  const localizeText = (text) => t(text.charAt(0).toUpperCase() + text.slice(1));

  const formatFuelLabel = (label) => {
    if (label === 'benzina/metano') {
      return t('Benzina/Metano');
    }

    if (label === 'GPL/metano') {
      return t('GPL/Metano');
    }

    if (label === 'elettrica/benzina') {
      return t('Elettrica/Benzina');
    }

    if (label === 'elettrica/Diesel') {
      return t('Elettrica/Diesel');
    }

    return localizeText(label);
  };

  const sortLookups = (data) => {
    const lookups = data;

    Object.entries(lookups).forEach(([key, values]) => {
      if (values.length > 1) {
        lookups[key] = values.sort((itemA, itemB) => ((itemA.id > itemB.id) ? 1 : -1));
      }
    });

    if (lookups.power.length > 1) {
      lookups.power = lookups.power.reverse();
    }

    return lookups;
  };

  const getFilteredLookups = (versions, quoteFilters) => {
    const lookups = {
      fuel: [],
      body: [],
      doors: [],
      gearbox: [],
      displacement: [],
      power: [],
      version: [],
    };

    if (typeof versions === 'undefined' || versions.length === 0) {
      return lookups;
    }

    const {
      techFuel: fuelId, techBody: bodyId, techDoors: doorsId,
      techDisplacement: displacementId, techPower: powerId,
      techGearbox: gearboxId, versionId: codeId,
    } = quoteFilters;
    let filteredVersionsData = versions;

    if (versions.length > 1) {
      const isValidField = (value) => typeof value === 'string' && value !== '';

      if (isValidField(fuelId)) {
        filteredVersionsData = filteredVersionsData.filter((item) => item.fuel === fuelId);
      }

      if (isValidField(bodyId) && bodyId) {
        filteredVersionsData = filteredVersionsData.filter((item) => item.body === bodyId);
      }

      if (isValidField(doorsId) && doorsId) {
        filteredVersionsData = filteredVersionsData.filter((item) => item.doors === doorsId);
      }

      if (isValidField(gearboxId)) {
        filteredVersionsData = filteredVersionsData.filter((item) => item.gearbox === gearboxId);
      }

      if (isValidField(displacementId)) {
        filteredVersionsData = filteredVersionsData.filter(
          (item) => item.displacement === displacementId,
        );
      }

      if (isValidField(powerId)) {
        filteredVersionsData = filteredVersionsData.filter(
          (item) => item.kwatt.toString() === powerId,
        );
      }

      if (isValidField(codeId) && codeId !== '0') {
        filteredVersionsData = filteredVersionsData.filter(
          (item) => item.family_code.toString() === codeId,
        );
      }
    }

    filteredVersionsData.forEach((version) => {
      const {
        body,
        fuel,
        doors,
        gearbox,
        displacement,
        kwatt: power,
        family_code: familyCode,
        name: versionName,
      } = version;

      if (body && !lookups.body.filter((item) => item.id === body).length) {
        lookups.body.push({
          id: body,
          text: localizeText(body),
        });
      }

      if (fuel && !lookups.fuel.filter((item) => item.id === fuel).length) {
        lookups.fuel.push({
          id: fuel,
          text: formatFuelLabel(fuel),
        });
      }

      if ((doors || doors === '0') && !lookups.doors.filter((item) => item.id === String(doors)).length) {
        lookups.doors.push({
          id: doors.toString(),
          text: doors.toString(),
        });
      }

      if (gearbox && !lookups.gearbox.filter((item) => item.id === gearbox).length) {
        lookups.gearbox.push({
          id: gearbox,
          text: localizeText(
            gearbox === 'semi automatico' ? 'semi-automatico' : gearbox,
          ),
        });
      }

      if (displacement && !lookups.displacement.filter((item) => item.id === displacement).length) {
        lookups.displacement.push({
          id: displacement.toString(),
          text: `${formatters.number(displacement, false)} ${t('cc')}`,
        });
      }

      if (power && !lookups.power.filter((item) => item.id === power.toString()).length) {
        lookups.power.push({
          id: power.toString(),
          text: `${power} ${t('KW')} / ${Math.ceil(parseInt(power, 10) * 1.3596216)} ${t('CV')}`,
        });
      }

      if (familyCode && !lookups.version.filter((item) => item.id === familyCode).length) {
        lookups.version.push({
          id: familyCode.toString(),
          text: versionName,
        });
      }
    });

    return sortLookups(lookups);
  };

  const prepareVersionsLookup = (versions) => {
    if (Object.entries(versions).length === 0) {
      // Return empty data
      return {};
    }

    const { values: versionsData } = versions;
    const lookups = {
      fuel: [],
      body: [],
      doors: [],
      gearbox: [],
    };

    // Buttons lookup setup
    versionsData.forEach((version) => {
      const {
        body,
        fuel,
        doors,
        gearbox,
      } = version;

      if (body && !lookups.body.filter((item) => item.id === body).length) {
        lookups.body.push({
          id: body,
          text: localizeText(body),
        });
      }

      if (fuel && !lookups.fuel.filter((item) => item.id === fuel).length) {
        lookups.fuel.push({
          id: fuel,
          text: formatFuelLabel(fuel),
        });
      }

      if ((doors || doors === '0') && !lookups.doors.filter((item) => item.id === doors).length) {
        lookups.doors.push({
          id: doors.toString(),
          text: doors.toString(),
        });
      }
      if (gearbox && !lookups.gearbox.filter((item) => item.id === gearbox).length) {
        lookups.gearbox.push({
          id: gearbox,
          text: localizeText(
            gearbox === 'semi automatico' ? 'semi-automatico' : gearbox,
          ),
        });
      }
    });

    // Comboboxes lookup setup
    const filteredLookups = getFilteredLookups(versionsData, quote);
    const { displacement, power, version } = filteredLookups;

    lookups.displacement = displacement;
    lookups.power = power;
    lookups.version = version;

    return sortLookups(lookups);
  };

  const versionsLookup = prepareVersionsLookup(rawVersionsData);
  const {
    fuel: fuelLookup, body: bodyLookup, doors: doorsLookup,
    gearbox: gearboxLookup, displacement: displacementLookup, power: powerLookup,
    version: versionLookup,
  } = versionsLookup;

  const isValidField = (lookup, fieldValue, customFieldValue = null) => {
    const isValid = typeof lookup === 'object'
      && lookup.length > 0
      && typeof fieldValue === 'string'
      && fieldValue !== '';

    if (typeof customFieldValue === 'string') {
      return isValid
        && (
          (fieldValue !== '0' && fieldValue !== '')
          || (fieldValue === '0' && typeof customFieldValue === 'string' && customFieldValue !== '')
        );
    }

    return isValid;
  };

  const getValidationState = () => (
    isValidField(fuelLookup, techFuel)
    && isValidField(bodyLookup, techBody)
    && isValidField(doorsLookup, String(techDoors))
    && isValidField(gearboxLookup, techGearbox)
    && isValidField(displacementLookup, techDisplacement)
    && isValidField(powerLookup, techPower)
    && isValidField(versionLookup, versionId, versionCustom)
  );

  const updatePage = (key, value, text, customValue = null) => {
    let newQuoteData = { ...quote };
    const { values: versionsData } = rawVersionsData;

    const { valueKey, textKey, customKey } = getQuoteKeys(key);
    newQuoteData[valueKey] = value;
    newQuoteData[textKey] = text;
    if (typeof customValue === 'string') {
      newQuoteData[customKey] = customValue;
    }

    let filteredLookups = getFilteredLookups(versionsData, newQuoteData);

    const {
      fuel: newFuelLookup,
      body: newBodyLookup,
      doors: newDoorsLookup,
      gearbox: newGearboxLookup,
      displacement: newDisplacementLookup,
      power: newPowerLookup,
      version: newVersionLookup,
    } = filteredLookups;
    let isQuoteValid = false;

    if (newDisplacementLookup.length === 0) {
      newQuoteData = {
        techFuel: '',
        techFuelText: '',
        techBody: '',
        techBodyText: '',
        techDoors: '',
        techDoorsText: '',
        techGearbox: '',
        techGearboxText: '',
        techDisplacement: '',
        techDisplacementText: '',
        techPower: '',
        techPowerText: '',
        versionId: '',
        versionText: '',
        versionCustom: '',
      };

      newQuoteData[valueKey] = value;
      newQuoteData[textKey] = text;
      if (typeof customValue === 'string') {
        newQuoteData[customKey] = customValue;
      }

      filteredLookups = getFilteredLookups(versionsData, newQuoteData);
    } else if (newDisplacementLookup.length === 1) {
      newQuoteData = {
        techFuel: newFuelLookup[0].id,
        techFuelText: newFuelLookup[0].text,
        techBody: newBodyLookup[0].id,
        techBodyText: newBodyLookup[0].text,
        techDoors: newDoorsLookup[0].id,
        techDoorsText: newDoorsLookup[0].text,
        techGearbox: newGearboxLookup[0].id,
        techGearboxText: newGearboxLookup[0].text,
        techDisplacement: newDisplacementLookup[0].id,
        techDisplacementText: newDisplacementLookup[0].text,
        techPower: newPowerLookup[0].id,
        techPowerText: newPowerLookup[0].text,
      };

      if (key === 'version') {
        // Update version if necessary
        // (version can't be forced)
        newQuoteData = {
          ...newQuoteData,
          versionId: value,
          versionText: text,
          versionCustom: customValue,
        };
      }

      // Check only version data for validation
      // because the other fields are forcely valid
      isQuoteValid = isValidField(
        newVersionLookup,
        newQuoteData.versionId,
        newQuoteData.versionCustom,
      );
    }

    if (newDisplacementLookup.length !== 1) {
      Object.entries(filteredLookups).forEach(([lookupKey, lookupValues]) => {
        // Adjust quote for single values lookup
        // excepting version because it contains always at least two values
        // (a valid lookup and custom version data handling)
        if (lookupKey !== 'version' && lookupValues.length === 1) {
          const {
            valueKey: newValueKey, textKey: newTextKey,
          } = getQuoteKeys(lookupKey);
          const { id: lookupValue, text: lookupText } = lookupValues[0];

          if (lookupValue !== newQuoteData[newValueKey]) {
            newQuoteData[newValueKey] = lookupValue;
            newQuoteData[newTextKey] = lookupText;
          }
        }
      });

      const {
        techFuel: newTechFuel, techBody: newTechBody,
        techDoors: newTechDoors, techGearbox: newTechGearbox,
        techDisplacement: newTechDisplacement, techPower: newTechPower,
        versionId: newVersionId, versionCustom: newVersionCustom,
      } = newQuoteData;

      isQuoteValid = (
        isValidField(fuelLookup, newTechFuel)
        && isValidField(bodyLookup, newTechBody)
        && isValidField(doorsLookup, String(newTechDoors))
        && isValidField(gearboxLookup, newTechGearbox)
        && isValidField(newDisplacementLookup, newTechDisplacement)
        && isValidField(newPowerLookup, newTechPower)
        && isValidField(newVersionLookup, newVersionId, newVersionCustom)
      );
    }

    dispatch(updateQuoteData(newQuoteData));
    dispatch(updateQuoteStepValidation(
      {
        idx: 1,
        isValid: isQuoteValid,
      },
    ));
  };

  useEffect(() => {
    const newQuoteData = {};

    // Set quote value for lookups with a single entry
    Object.entries(versionsLookup).forEach(([lookupKey, lookupValues]) => {
      // Adjust quote for single values lookup
      // excepting version because it contains always at least two values
      // (a valid lookup and custom version data handling)
      if (lookupKey !== 'version' && lookupValues.length === 1) {
        const { valueKey, textKey } = getQuoteKeys(lookupKey);
        const { id: value, text } = lookupValues[0];

        newQuoteData[valueKey] = value;
        newQuoteData[textKey] = text;
      }
    });

    if (Object.entries(newQuoteData).length > 0) {
      dispatch(updateQuoteData(newQuoteData));
    }
    dispatch(updateQuoteStepValidation(
      {
        idx: 1,
        isValid: getValidationState(),
      },
    ));
  }, [rawVersionsData]);

  return (
    <div style={{ textAlign: 'left' }}>
      { fuelLookup && fuelLookup.length
        && (
          <TechLookup
            identifier="fuel"
            title={t('searchv3.title.fuel')}
            icon={<GasStation />}
            items={fuelLookup}
            value={techFuel}
            update={updatePage}
            message={t('Please select a fuel.')}
          />
        )}
      { bodyLookup && bodyLookup.length
        && (
          <TechLookup
            identifier="body"
            title={t('Body')}
            icon={<BodyType />}
            items={bodyLookup}
            value={techBody}
            update={updatePage}
            message={t('Please select a body.')}
          />
        )}
      { doorsLookup && doorsLookup.length
        && (
          <TechLookup
            identifier="doors"
            title={t('Doors')}
            icon={<Doors />}
            items={doorsLookup}
            value={String(techDoors)}
            update={updatePage}
            message={t('Please select a doors.')}
          />
        )}
      { gearboxLookup && gearboxLookup.length
        && (
          <TechLookup
            identifier="gearbox"
            title={t('Gearbox')}
            icon={<Transmission />}
            items={gearboxLookup}
            value={techGearbox}
            update={updatePage}
            message={t('Please select a gearbox type.')}
          />
        )}
      { displacementLookup && displacementLookup.length
        && (
          <TechLookup
            identifier="displacement"
            title={t('Displacement')}
            icon={<Gears />}
            items={displacementLookup}
            value={techDisplacement}
            update={updatePage}
            type="combobox"
            message={t('Please select a displacement.')}
            hide={displacementLookup.length === 1 && displacementLookup[0].id === '0000'}
          />
        )}
      { powerLookup && powerLookup.length
        && (
          <TechLookup
            identifier="power"
            title={t('Power')}
            icon={<Speedometer />}
            items={powerLookup}
            value={techPower}
            update={updatePage}
            type="combobox"
            message={t('Please select a power.')}
          />
        )}
      { versionLookup && versionLookup.length
        && (
          <TechLookup
            identifier="version"
            title={t('Configuration')}
            icon={<Configuration />}
            items={versionLookup}
            value={versionId}
            manageCustom
            customValue={versionCustom}
            customSelectLabel={t('My set up is not present')}
            customTextLabel={t('Other configuration')}
            update={updatePage}
            type="combobox"
            message={t('Please select a configuration.')}
          />
        )}
    </div>
  );
}

export default Tech;
